import type { IChatHeader } from 'features/Chat/Chat.types'

/**
 * Finds the existing thread whose participants are exactly the people picked in
 * the compose screen, so messaging someone you already talk to continues that
 * conversation instead of starting a second one beside it.
 *
 * The comparison is on "everyone in the thread except me", NOT on the thread's
 * `receiverIds`. A thread stores who sent the opening message and who received
 * it, so a conversation the other person started has `receiverIds: [me]` and
 * carries them in `senderId`. Matching on `receiverIds` alone therefore missed
 * every thread the other person opened, and composing to them created a
 * duplicate stream that split the history in two.
 */
const getEventIdForReceiverIds = (
	chatHeaders: IChatHeader[],
	receiverIds: string[],
	userId?: string,
) => {
	// Copy before sorting: sort mutates, and the input is Redux/context state.
	// Ids are normalised because a populated participant is an object, and
	// String(object) is "[object Object]", which silently matches nothing.
	const asId = (value: any) => String(value?._id ?? value ?? '')
	const key = (ids: any[]) => [...new Set(ids.map(asId))].filter(Boolean).sort().join()

	const others = (header: IChatHeader) =>
		[header.senderId?._id, ...(header.receiverIds || []).map(r => r?.receiverId?._id)]
			.filter(Boolean)
			.map(String)
			.filter(id => id !== String(userId))

	const wanted = key(receiverIds || [])
	if (!wanted) return undefined

	const match = (chatHeaders || []).find(header => key(others(header)) === wanted)

	return match ? match._id : undefined
}

export default getEventIdForReceiverIds
